import type { Rng } from "../rng";
import {
	type BaseState,
	type GameDefinition,
	type MoveOutcome,
	type PlayerIndex,
	reject,
	resultFromScores,
} from "../types";

export const LADDER_RUNGS = 6;
/** However slowly it goes, the game is over after this many words. */
export const LADDER_WORDS = 10;
export const LADDER_WORD_MS = 20_000;
export const LADDER_REVEAL_MS = 2000;

/** Short, common, and spelled the way they sound. */
const WORDS = [
	"planet",
	"rocket",
	"jungle",
	"puzzle",
	"dragon",
	"basket",
	"winter",
	"silver",
	"garden",
	"monkey",
	"pencil",
	"bridge",
	"castle",
	"sunset",
	"tunnel",
	"yellow",
	"pirate",
	"rabbit",
	"school",
	"orange",
] as const;

export interface TypingLadderState extends BaseState {
	kind: "typingladder";
	word: string;
	/** The letter tiles on offer, shuffled. */
	tiles: string[];
	rung: number;
	phase: "spelling" | "reveal";
	endsAt: number;
	/** How far each player has got through the word. */
	progress: [number, number];
	lastNote: string | null;
}

export interface TypingLadderMove {
	tile: number;
}

function nextWord(rng: Rng, state: TypingLadderState, now: number): TypingLadderState {
	const word = WORDS[rng.int(WORDS.length)];
	return {
		...state,
		word,
		// The word's own letters, jumbled, plus nothing else to confuse things.
		tiles: rng.shuffle(word.split("")),
		phase: "spelling",
		endsAt: now + LADDER_WORD_MS,
		progress: [0, 0],
		lastNote: null,
	};
}

export const typingLadder: GameDefinition<TypingLadderState, TypingLadderMove> = {
	id: "typingladder",
	category: "racing",
	title: "Typing Ladder",
	emoji: "🪜",
	blurb: "Tap the letters in order to spell the word. Fastest speller climbs.",
	howTo: [
		"A word appears, with its letters jumbled below.",
		"Tap the letters in the right order to spell it.",
		"Tap a wrong letter and you start that word again.",
		"First to finish the word climbs a rung. Six rungs wins.",
	],

	create(rng, now) {
		return nextWord(
			rng,
			{
				kind: "typingladder",
				word: "",
				tiles: [],
				rung: 0,
				phase: "spelling",
				endsAt: now,
				progress: [0, 0],
				lastNote: null,
				// Both players race the same word.
				turn: null,
				scores: [0, 0],
				result: null,
			},
			now,
		);
	},

	parseMove(raw) {
		if (typeof raw !== "object" || raw === null) return null;
		const tile = (raw as { tile?: unknown }).tile;
		if (typeof tile !== "number" || !Number.isInteger(tile)) return null;
		if (tile < 0 || tile > 32) return null;
		return { tile };
	},

	apply(state, player, move, now): MoveOutcome<TypingLadderState> {
		if (state.result) return reject("This game is already finished.");
		if (state.phase !== "spelling") return reject("Hold on — the next word is coming.");

		const letter = state.tiles[move.tile];
		if (letter === undefined) return reject("That letter isn't there.");

		const progress: [number, number] = [...state.progress];
		const wanted = state.word[progress[player]];

		if (letter !== wanted) {
			// Wrong letter: back to the start of this word, but no points lost.
			progress[player] = 0;
			return {
				ok: true,
				state: { ...state, progress, lastNote: "Oops — start that word again!" },
			};
		}

		progress[player] += 1;
		if (progress[player] < state.word.length) {
			return { ok: true, state: { ...state, progress } };
		}

		// Word finished: a rung for them.
		const scores: [number, number] = [...state.scores];
		scores[player] += 1;
		const finished = scores[player] >= LADDER_RUNGS;

		return {
			ok: true,
			state: {
				...state,
				progress,
				scores,
				phase: "reveal",
				endsAt: now + LADDER_REVEAL_MS,
				lastNote: `"${state.word}" spelled!`,
				result: finished ? resultFromScores(scores) : null,
			},
		};
	},

	legalMoves(state) {
		if (state.result || state.phase !== "spelling") return [];
		// Only the correct next letter is worth tapping, but any tile is allowed.
		return state.tiles.map((_, tile) => ({ tile }));
	},

	tick(state, now) {
		if (state.result || now < state.endsAt) return null;

		const scores: [number, number] = [...state.scores];
		// A word nobody finished still goes to whoever got closest.
		if (state.phase === "spelling" && state.progress[0] !== state.progress[1]) {
			scores[state.progress[0] > state.progress[1] ? 0 : 1] += 1;
		}

		const rung = state.rung + 1;
		if (scores[0] >= LADDER_RUNGS || scores[1] >= LADDER_RUNGS || rung >= LADDER_WORDS) {
			return { ...state, scores, phase: "reveal", result: resultFromScores(scores) };
		}

		// Seeded from the word itself, so the sequence stays reproducible.
		const seed = state.word.split("").reduce((total, char) => total + char.charCodeAt(0), 0);
		const rng = pseudoRng(seed + rung + 1);
		return nextWord(rng, { ...state, rung, scores }, now);
	},

	nextTickAt(state) {
		return state.result ? null : state.endsAt;
	},

	redact(state, viewer) {
		const them: PlayerIndex = viewer === 0 ? 1 : 0;
		// How far they've got is a number, not the letters they pressed.
		const progress: [number, number] = [...state.progress];
		progress[them] = Math.min(progress[them], state.word.length);
		return { ...state, progress };
	},
};

/** A tiny local generator, so `tick` stays pure without threading an Rng in. */
function pseudoRng(seed: number): Rng {
	let value = seed >>> 0;
	const next = () => {
		value = (value * 1664525 + 1013904223) >>> 0;
		return value / 4294967296;
	};
	const int = (max: number) => Math.floor(next() * max);
	const shuffle = <T>(items: readonly T[]): T[] => {
		const copy = [...items];
		for (let i = copy.length - 1; i > 0; i--) {
			const j = int(i + 1);
			const a = copy[i];
			copy[i] = copy[j];
			copy[j] = a;
		}
		return copy;
	};
	return { next, int, shuffle, sample: (items, count) => shuffle(items).slice(0, count) };
}
